import { Injectable } from "@angular/core";
import { ApplicationInsightsService } from "../application-insights/application-insights.service";

@Injectable({
  providedIn: 'root',
})
export class ExcelDataMapperService {

  constructor(private appInsights: ApplicationInsightsService) {

  }
  public buildFilteredData(
    matrix: string[][],
    mapObject: Record<string, { sqlCol: string; dataType: string; required?: boolean;validate?: (value: any) => boolean }>,
    sheetName: string,
    options?: {
      skipRows?: number;
      removeLastRow?: boolean;
      skipRowsWhenAllMappedColumnsBlank?: boolean;
      ignoreBlankCheckColumns?: string[];
    }
  ): { sqlHeaders: string[]; tableRows: (string | number | boolean)[][] } {
    try {
      if (!matrix || matrix.length === 0) throw new Error('empty matrix');

      const skipRows = options?.skipRows ?? 5;
      const removeLastRow = options?.removeLastRow ?? true;

      matrix = matrix.slice(skipRows);

      const headerRow = matrix[0];
      let dataRows = matrix.slice(1);
      if (removeLastRow && dataRows.length > 0) {
        dataRows = dataRows.slice(0, -1);
      }

      // Keep only headers that exist in mapObject
      type ColumnInfo = { sqlCol: string; sourceIndex: number; dataType: string; required?: boolean; validate?: (value: any) => boolean };
      const columns: ColumnInfo[] = [];

      headerRow.forEach((header, idx) => {
        const trimmed = this.normalizeHeaderCell(header);
        const mapping = mapObject[trimmed];
        if (mapping) {
          columns.push({
            sqlCol: mapping.sqlCol,
            sourceIndex: idx,
            dataType: mapping.dataType,
            required: mapping.required,
            validate: mapping.validate
          });
        }
      });

      if (columns.length === 0) throw new Error('No matching columns found.');

      // Unique SQL column list (if header duplicates, keep first occurrence)
      const sqlHeaders: string[] = [];
      const seen = new Set<string>();
      for (const c of columns) {
        if (!seen.has(c.sqlCol)) {
          sqlHeaders.push(c.sqlCol);
          seen.add(c.sqlCol);
        }
      }

      // Build the 2-D data array in sqlHeaders order
      const missingRequired: Record<string, number[]> = {};
      const invalidDateValues: Record<string, number[]> = {};

      const blankCheckIgnoredColumns = new Set(
        (options?.ignoreBlankCheckColumns ?? []).map((column) => column.trim().toLowerCase())
      );

      const hasRequiredColumns = columns.some((column) => column.required);

      const columnsForBlankCheck = hasRequiredColumns
        ? columns.filter((column) => column.required)
        : columns;

      const blankCheckColumnIndexes = [
        ...new Set(
          columnsForBlankCheck
            .filter((column) => {
              const excelColumnName = this.normalizeHeaderCell(headerRow[column.sourceIndex]).toLowerCase();
              return !blankCheckIgnoredColumns.has(excelColumnName);
            })
            .map((column) => column.sourceIndex)
        )
      ];

      const rowsForProcessing = dataRows
        .map((row, index) => ({ row, excelRelativeRow: index + 1 }))
        .filter(({ row }) => {
          if (!options?.skipRowsWhenAllMappedColumnsBlank) {
            return true;
          }

          if (blankCheckColumnIndexes.length === 0) {
            return true;
          }

          return !blankCheckColumnIndexes.every((columnIndex) => {
            const rawValue = row[columnIndex] ?? '';
            const normalizedRawValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
            return this.isEmptyValue(normalizedRawValue);
          });
        });

      const tableRows: (string | number | boolean)[][] = rowsForProcessing.map(({ row, excelRelativeRow }) => {
        return sqlHeaders.map((sql) => {
          // Find the first ColumnInfo that maps to this sqlHeader
          const ci = columns.find((c) => c.sqlCol === sql);
          if (!ci) return '';

          const rawValue = row[ci.sourceIndex] ?? '';

          // Required field check
          const normalizedRawValue = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
          if (ci.required && (normalizedRawValue === null || normalizedRawValue === undefined || normalizedRawValue === '')) {
            const excelColumn = this.normalizeHeaderCell(headerRow[ci.sourceIndex]);
            if (!missingRequired[excelColumn]) missingRequired[excelColumn] = [];
            missingRequired[excelColumn].push(excelRelativeRow);
          }

          if (
            ci.dataType === 'datetime' &&
            !this.isEmptyValue(normalizedRawValue) &&
            !this.isValidDateValue(rawValue, ci.validate)
          ) {
            const excelColumn = this.normalizeHeaderCell(headerRow[ci.sourceIndex]);
            if (!invalidDateValues[excelColumn]) invalidDateValues[excelColumn] = [];
            invalidDateValues[excelColumn].push(excelRelativeRow);
          }

          return this.validateDataType(rawValue, ci.dataType, ci.validate);
        });
      });

      const validationErrors: string[] = [];
      for (const [col, rows] of Object.entries(missingRequired)) {
        const adjustedRows = rows.map((row) => row + skipRows + 1);
        validationErrors.push(`Required field "${col}" is missing in rows: ${adjustedRows.join(', ')}`);
      }

      for (const [col, rows] of Object.entries(invalidDateValues)) {
        const adjustedRows = rows.map((row) => row + skipRows + 1);
        validationErrors.push(`Invalid date format for "${col}" in rows: ${adjustedRows.join(', ')}`);
      }

      if (validationErrors.length > 0) {
        const errorMsg = `Validation errors in sheet "${sheetName}":\n${validationErrors.join('\n')}`;
        this.appInsights.trackException(new Error(errorMsg), { location: "ExcelDataMapperService.buildFilteredData", sheetName });
        this.appInsights.trackEvent("ExcelDataMapperService.buildFilteredData.requiredFieldMissing", { error: errorMsg, sheetName });
        throw new Error(errorMsg);
      }

      return { sqlHeaders, tableRows };
    } catch (error: any) {
      console.error('Error in buildFilteredData:', error);
      this.appInsights.trackException(error, { location: "ExcelDataMapperService.buildFilteredData" });
      this.appInsights.trackEvent("ExcelDataMapperService.buildFilteredData.error", { error: error?.message || error });
      throw error;
    }
  }

  public validateDataType(value: any, dataType: string, customValidator?: (value: any) => boolean): string | number | boolean {
    try {
      switch (dataType) {
        case 'string':
          return value.toString();
        case 'number':
          const num = parseFloat(value);
          return isNaN(num) ? 0.0 : num;
        case 'boolean':
          return this.isEmptyValue(value) ? false : true;
        case 'datetime':
          const normalizedDateValue = this.normalizeDateInput(value);
          const date = new Date(normalizedDateValue);
          const utcDate = isNaN(date.getTime()) ? '' : new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds())).toISOString();
          if (utcDate && !isNaN(Date.parse(utcDate))) {
            //  Validate the date with custom validator 
            if (customValidator && !customValidator(normalizedDateValue)) {
              this.validateDueDate(normalizedDateValue);
              this.appInsights.trackEvent("ExcelDataMapperService.validateDataType.invalidCustomDate", { value: normalizedDateValue, utcDate });
              return '';
            }
            return utcDate;
          } else {
            this.appInsights.trackEvent("ExcelDataMapperService.validateDataType.invalidDate", { value, utcDate });
            return '';
          }
        default:
          return value;
      }
    } catch (error: any) {
      console.error('Error in validateDataType:', error);
      this.appInsights.trackException(error, { location: "ExcelDataMapperService.validateDataType" });
      this.appInsights.trackEvent("ExcelDataMapperService.validateDataType.error", { error: error?.message || error });
      throw error;
    }
  }

  public validateDueDate(value: any): boolean {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  private isEmptyValue(value: any): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
  }

  private isValidDateValue(value: any, customValidator?: (value: any) => boolean): boolean {
    const normalizedDateValue = this.normalizeDateInput(value);
    const parsedDate = normalizedDateValue instanceof Date ? normalizedDateValue : new Date(normalizedDateValue);
    const isValidDate = !isNaN(parsedDate.getTime());

    if (!isValidDate) {
      return false;
    }

    if (customValidator) {
      return customValidator(normalizedDateValue);
    }

    return true;
  }

  private normalizeDateInput(value: any): any {
    if (typeof value === 'number') {
      // Excel serial date to JS Date conversion.
      return new Date((value - 25569) * 86400 * 1000);
    }

    return value;
  }

  private normalizeHeaderCell(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }
}